import React, { useState, useEffect } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
  Vault, Plus, Trash, ShieldCheck, WarningCircle, ArrowsClockwise,
  Key, CheckCircle, XCircle, Lock, X
} from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function APIVault({ onVerifiedChange }) {
  const [services, setServices] = useState([]);
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ service_id: "", label: "", key: "" });
  const [verifyingId, setVerifyingId] = useState(null);

  const loadAll = async () => {
    try {
      const [svc, kk] = await Promise.all([
        api.get("/vault/services"),
        api.get("/vault/keys"),
      ]);
      setServices(svc.data.services);
      setKeys(kk.data.keys);
      if (onVerifiedChange) {
        onVerifiedChange(kk.data.keys.some((k) => k.verified));
      }
    } catch (e) { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); }, []);

  const addKey = async (e) => {
    e.preventDefault();
    if (!form.service_id || !form.key) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/vault/keys", form);
      setKeys([data, ...keys]);
      setShowAdd(false);
      setForm({ service_id: "", label: "", key: "" });
      if (data.verified) {
        toast.success(`Green-Light: ${data.service.name} verified.`);
        if (onVerifiedChange) onVerifiedChange(true);
      } else {
        toast.error(`Hull Breach: ${data.verification_message}`);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Vault rejected key.");
    } finally {
      setSubmitting(false);
    }
  };

  const reverify = async (id) => {
    setVerifyingId(id);
    try {
      const { data } = await api.post(`/vault/keys/${id}/verify`);
      setKeys(keys.map((k) => k.id === id ? {
        ...k, verified: data.verified, verification_message: data.message,
        verification_status_code: data.status_code, last_verified_at: new Date().toISOString(),
      } : k));
      if (data.verified) toast.success("Green-Light reaffirmed.");
      else toast.error(data.message);
    } catch (e) {
      toast.error("Re-verification failed.");
    } finally {
      setVerifyingId(null);
    }
  };

  const removeKey = async (id) => {
    if (!window.confirm("Remove this key from the vault?")) return;
    try {
      await api.delete(`/vault/keys/${id}`);
      const remaining = keys.filter((k) => k.id !== id);
      setKeys(remaining);
      toast.success("Key purged from vault.");
      if (onVerifiedChange) onVerifiedChange(remaining.some((k) => k.verified));
    } catch { toast.error("Failed to remove key."); }
  };

  const selectedService = services.find((s) => s.id === form.service_id);
  const verifiedCount = keys.filter((k) => k.verified).length;

  return (
    <div className="surface-card p-6 md:p-8" data-testid="api-vault">
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 border border-[#FFD700]/40 flex items-center justify-center flex-shrink-0">
            <Vault size={22} weight="duotone" className="text-[#FFD700]" />
          </div>
          <div>
            <div className="overline mb-1">// Sovereign API Vault</div>
            <h3 className="text-xl font-bold text-white">Encrypted Key Armor</h3>
            <p className="text-sm text-white/60 font-light mt-1">
              Director's voice: "Drop your keys here, Commander. Vault encrypts at rest, verifies live, and Green-Lights your next boarding step."
            </p>
          </div>
        </div>
        {!showAdd && (
          <button
            onClick={() => setShowAdd(true)}
            data-testid="vault-add-btn"
            className="btn-gold px-5 py-2.5 text-xs flex items-center gap-2 flex-shrink-0"
          >
            <Plus size={14} weight="bold" /> Add Key
          </button>
        )}
      </div>

      {/* Status summary */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        <StatusTile label="Total Keys" value={keys.length} />
        <StatusTile label="Green-Lit" value={verifiedCount} gold />
        <StatusTile label="Stage 2 Status" value={verifiedCount > 0 ? "UNLOCKED" : "PENDING"} mono />
      </div>

      {/* Add form */}
      <AnimatePresence>
        {showAdd && (
          <motion.form
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            onSubmit={addKey}
            className="surface-card p-5 mb-6 space-y-4 overflow-hidden border-[#FFD700]/30"
            data-testid="vault-add-form"
          >
            <div className="flex items-center justify-between">
              <div className="overline text-[0.6rem]">// New Vault Entry</div>
              <button type="button" onClick={() => setShowAdd(false)} className="text-white/40 hover:text-white">
                <X size={16} />
              </button>
            </div>

            <div>
              <Label className="overline text-[0.6rem] mb-2 block">Service</Label>
              <Select value={form.service_id} onValueChange={(v) => setForm({ ...form, service_id: v })}>
                <SelectTrigger data-testid="vault-service-select" className="bg-[#0c0c0c] border-white/10 rounded-none h-11 text-white">
                  <SelectValue placeholder="Select a service to armor..." />
                </SelectTrigger>
                <SelectContent className="bg-[#0c0c0c] border-white/10 text-white">
                  {services.map((s) => (
                    <SelectItem key={s.id} value={s.id} data-testid={`vault-svc-${s.id}`}>
                      {s.name} <span className="text-white/40">— {s.category}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedService && (
              <>
                <div>
                  <Label className="overline text-[0.6rem] mb-2 block">Label (optional)</Label>
                  <Input
                    data-testid="vault-label-input"
                    value={form.label}
                    onChange={(e) => setForm({ ...form, label: e.target.value })}
                    placeholder={`Primary ${selectedService.name}`}
                    className="bg-[#0c0c0c] border-white/10 rounded-none h-11 text-white"
                  />
                </div>

                <div>
                  <Label className="overline text-[0.6rem] mb-2 block">{selectedService.key_label}</Label>
                  <Input
                    data-testid="vault-key-input"
                    type="password"
                    value={form.key}
                    onChange={(e) => setForm({ ...form, key: e.target.value })}
                    placeholder={`Paste your ${selectedService.name} key...`}
                    required
                    className="font-mono bg-[#0c0c0c] border-white/10 rounded-none h-11 text-white tracking-wider"
                  />
                  <div className="text-[0.65rem] font-mono uppercase tracking-widest text-white/40 mt-2 flex items-center gap-2">
                    <Lock size={10} /> Encrypted with Fernet (AES-128 + HMAC) at rest
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={submitting || !form.key}
                  data-testid="vault-submit-btn"
                  className="w-full btn-gold py-3 text-xs flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {submitting ? "Verifying with service..." : (<><ShieldCheck size={14} /> Armor & Verify Key</>)}
                </button>
              </>
            )}
          </motion.form>
        )}
      </AnimatePresence>

      {/* Keys list */}
      {loading ? (
        <div className="py-8 text-center text-white/40 overline">Decrypting vault...</div>
      ) : keys.length === 0 ? (
        <div className="py-12 text-center" data-testid="vault-empty">
          <Key size={40} weight="duotone" className="text-white/20 mx-auto mb-3" />
          <p className="text-white/50 text-sm">Vault empty. Drop your first key to Green-Light Stage 2.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="vault-keys-list">
          {keys.map((k) => (
            <div
              key={k.id}
              data-testid={`vault-key-${k.service_id}`}
              className={`flex items-center justify-between gap-3 p-4 border transition-all ${
                k.verified ? "border-[#FFD700]/30 bg-[#FFD700]/5" : "border-red-900/30 bg-red-950/10"
              }`}
            >
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <div className="flex-shrink-0">
                  {k.verified ? (
                    <CheckCircle size={22} weight="duotone" className="text-[#FFD700]" />
                  ) : (
                    <XCircle size={22} weight="duotone" className="text-red-400" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-white truncate">{k.service?.name || k.service_id}</span>
                    <span className="font-mono text-[0.6rem] uppercase tracking-widest px-2 py-0.5 border border-white/10 text-white/60">
                      {k.service?.category}
                    </span>
                    {k.verified && (
                      <span className="font-mono text-[0.55rem] uppercase tracking-widest px-2 py-0.5 bg-[#FFD700]/20 text-[#FFD700]">
                        GREEN-LIGHT
                      </span>
                    )}
                  </div>
                  <div className="font-mono text-xs text-white/70 mt-1 truncate">{k.masked_key}</div>
                  <div className={`text-[0.65rem] font-mono mt-1 ${k.verified ? "text-white/40" : "text-red-400/80"}`}>
                    {k.verification_message}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={() => reverify(k.id)}
                  disabled={verifyingId === k.id}
                  data-testid={`vault-verify-${k.id}`}
                  className="p-2 text-white/60 hover:text-[#FFD700] disabled:opacity-50 transition-colors"
                  title="Re-verify"
                >
                  <ArrowsClockwise size={16} className={verifyingId === k.id ? "animate-spin" : ""} />
                </button>
                <button
                  onClick={() => removeKey(k.id)}
                  data-testid={`vault-delete-${k.id}`}
                  className="p-2 text-white/40 hover:text-red-400 transition-colors"
                  title="Remove"
                >
                  <Trash size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const StatusTile = ({ label, value, gold, mono }) => (
  <div className="border border-white/5 p-3 text-center">
    <div className="overline text-[0.55rem] mb-1">{label}</div>
    <div className={`${mono ? "font-mono text-sm uppercase tracking-widest" : "text-2xl font-bold"} ${gold ? "text-[#FFD700]" : "text-white"}`}>
      {value}
    </div>
  </div>
);
